import type { ChangeEvent } from 'react';
import { isNumber } from '@noths/polaris-client-utils';
import type { Dictionary, Primitive } from '@noths/polaris-dev-ts-recipes';
import * as R from 'ramda';

import type { AsFormValue, FormChangeEvent } from './types/form';
import type { GenericValidator, RuleValidatorResult, ValidationRules } from './types/validation';
import * as validators from './validators';

type Validators = typeof validators;

const isPrimitiveRule = (val: Primitive | Dictionary): val is NonNullable<Primitive> =>
  typeof val === 'number' || typeof val === 'boolean' || typeof val === 'string';

const asNumberOrString = (val: number | string) => (isNumber(val) ? Number(val) : val);

const getRuleValidatorResult = <TState extends Dictionary<TState>>(
  value: TState[keyof TState],
  rule: NonNullable<ValidationRules<TState>[keyof ValidationRules<TState>]>,
  validator: Validators[keyof Validators],
  formValues: TState,
): RuleValidatorResult => {
  const validationResult: RuleValidatorResult = { isInvalid: false, error: null };

  if (typeof rule === 'function' || isPrimitiveRule(rule)) {
    validationResult.isInvalid = (validator as GenericValidator)(value, rule, formValues);
  } else {
    validationResult.isInvalid = (validator as GenericValidator)(value, rule.value, formValues);
    validationResult.error = rule.message;
  }

  return validationResult;
};

export const getFieldValidation = <TState extends Dictionary<TState>>(
  fieldRules: ValidationRules<TState>,
  value: TState[keyof TState],
  formValues: TState,
) =>
  Object.keys(fieldRules).reduce(
    (currentValidation, ruleName): RuleValidatorResult => {
      const rule = fieldRules[ruleName as keyof ValidationRules<TState>]!;
      const ruleValidator = validators[ruleName as keyof ValidationRules<TState>];
      const valueToValidate = typeof value === 'number' ? String(value) : value;

      const validationResult = getRuleValidatorResult(
        valueToValidate as TState[keyof TState],
        rule,
        ruleValidator,
        formValues,
      );

      return validationResult.isInvalid ? validationResult : currentValidation;
    },
    { isInvalid: false, error: null } as RuleValidatorResult,
  );

export const isCheckboxInput = (e: FormChangeEvent): e is ChangeEvent<HTMLInputElement> =>
  e.target.type === 'checkbox';

export const hasNestedPath = (value: string) => typeof value === 'string' && value.includes('.');

export const getFieldNameAsList = R.compose(R.map(asNumberOrString), R.split('.'));

export const getDeeplyUpdatedState = <TState extends Dictionary>(
  updatePath: Array<string | number>,
  value: AsFormValue<TState>,
  state: TState,
) => R.assocPath<AsFormValue<TState>, TState>(updatePath, value)(state);
